import {toInteger} from 'lodash';
import sortBy from 'lodash/sortBy';

import {htmlToTokens} from 'app/string/htmldiff';

import {
  adjustHighlightParams,
  TEXT_HIGHLIGHT_CLASS_PREFIX,
} from './highlightUtils';
import {generateTextNodeInSpan, swapTextNodeWithSpanNodes} from './nodes';

import type {
  HtmlDocType,
  TextNodeTypeWithParent,
} from '../nodeProcessors/node-processors-types';
import type {TextHighlightType} from 'app/comments/types';

type SplitType = {
  tokenNumber: number;
  tokenTextOffset: number;
};
export function splitNodeForTokenHighlights(
  node: HtmlDocType,
  textChild: TextNodeTypeWithParent,
  highlights: Array<TextHighlightType>
): HtmlDocType {
  if (node.attribs['data-split'] != null) {
    return node; // Already processed
  }

  let startToken = toInteger(node.attribs['data-htmldiff-start']);
  let endToken = toInteger(node.attribs['data-htmldiff-end']);

  const splitPoints: SplitType[] = [];

  // Collect all token-based split points from highlights
  for (const highlight of highlights) {
    const {startParams, endParams} = highlight;

    if (startParams.token >= startToken && startParams.token <= endToken) {
      splitPoints.push({
        tokenNumber: startParams.token,
        tokenTextOffset: startParams.tokenTextOffset,
      });
    }

    if (endParams.token >= startToken && endParams.token <= endToken) {
      splitPoints.push({
        tokenNumber: endParams.token,
        tokenTextOffset: endParams.tokenTextOffset,
      });
    }
  }

  if (splitPoints.length === 0) {
    return node; // Nothing to split
  }

  const textTokens = htmlToTokens(textChild.data);
  const originalTokenCount = endToken - startToken;

  // Adjust if token count mismatch suggests extra outer tokens
  if (textTokens.length < originalTokenCount) {
    startToken += 1;
    endToken -= 1;
  }

  const sortedSplits = sortBy(splitPoints, ['tokenNumber', 'tokenTextOffset']);

  const newTextNodes: HtmlDocType[] = [];
  let processedLength = 0;
  let lastSplit: SplitType | undefined;

  for (const split of sortedSplits) {
    let tokenCursor = 0;
    let accumulatedText = '';

    while (tokenCursor < textTokens.length) {
      const token = textTokens[tokenCursor];
      const tokenAbsoluteIndex = startToken + tokenCursor;

      if (tokenAbsoluteIndex === split.tokenNumber) {
        const substringOffset =
          lastSplit && lastSplit.tokenNumber === tokenAbsoluteIndex
            ? split.tokenTextOffset - lastSplit.tokenTextOffset
            : split.tokenTextOffset;

        const tokenText = token.string.substring(0, substringOffset);
        accumulatedText += tokenText;

        if (accumulatedText.length === 0) {
          break; // Skip empty spans
        }

        newTextNodes.push(
          generateTextNodeInSpan(
            accumulatedText,
            startToken,
            tokenAbsoluteIndex,
            lastSplit?.tokenTextOffset ?? 0
          )
        );

        startToken = tokenAbsoluteIndex;
        processedLength += accumulatedText.length;

        // Mutate token array in-place: remove consumed tokens
        textTokens.splice(0, tokenCursor);

        const leftover = token.string.substring(substringOffset);
        if (leftover.length > 0) {
          textTokens[0].string = leftover;
        } else {
          textTokens.shift();
          startToken += 1;
          lastSplit = undefined;
        }

        lastSplit = split;
        break;
      }

      accumulatedText += token.string;
      tokenCursor += 1;
    }
  }

  // Push any remaining text as the final span
  const remainingText = textChild.data.substring(processedLength);
  if (remainingText.length > 0) {
    newTextNodes.push(
      generateTextNodeInSpan(
        remainingText,
        startToken,
        endToken,
        lastSplit?.tokenTextOffset ?? 0
      )
    );
  }

  // Replace original text node with the split span-wrapped ones
  return swapTextNodeWithSpanNodes(textChild, newTextNodes);
}

export function highlightTokenHtmlNode(
  parent: HtmlDocType,
  highlight: TextHighlightType
): HtmlDocType {
  const parentAttributes = parent.attribs;
  if (
    (parentAttributes['class'] || '').indexOf(
      `${TEXT_HIGHLIGHT_CLASS_PREFIX}${highlight.id}`
    ) !== -1
  ) {
    return parent;
  }
  let startToken = toInteger(parent.attribs['data-htmldiff-start']);
  let endToken = toInteger(parent.attribs['data-htmldiff-end']);
  let tokenTextOffset = toInteger(
    parent.attribs['data-htmldiff-offset'] || '0'
  );

  if (
    startToken >= highlight.startParams.token &&
    endToken <= highlight.endParams.token
  ) {
    if (
      startToken === highlight.startParams.token &&
      tokenTextOffset < highlight.startParams.tokenTextOffset
    ) {
      return parent;
    }

    if (
      startToken === highlight.endParams.token &&
      tokenTextOffset >= highlight.endParams.tokenTextOffset
    ) {
      return parent;
    }

    adjustHighlightParams(parentAttributes, highlight);
  }
  return parent;
}
